import { useMemo, useRef } from 'react'

import { Tag } from 'antd'

import { queryEntrustBidRecordListApi } from '@eastcoal/api/trade/bidRecordMember/queryEntrustBidRecordList'
import { queryParticipantBidRecordListApi } from '@eastcoal/api/trade/bidRecordMember/queryParticipantBidRecordList'
import { AuctionMemberPageItem } from '@eastcoal/api/trade/interface'

import { DraggableModal } from '@eastcoal/common/components/DraggableModal'
import { RemoteTable } from '@eastcoal/common/components/RemoteTable'
import { RemoteTableStoreRef } from '@eastcoal/common/components/RemoteTable/Store/interface'
import { pretty } from '@eastcoal/common/utils/decimal'
import { pick } from '@eastcoal/common/utils/object'

import {
  BidRecordStatus,
  BidRecordStatusColorMap,
  BidRecordStatusEntrustTextMap,
  BidRecordStatusParticipantTextMap
} from '@eastcoal/shared/enums/BidRecordStatus'
import { getSeatNo } from '@eastcoal/shared/utils/auction'

import { AuctionMemberPageStatus } from '../../../enums/AuctionMemberPageStatus'
import { useAuctionListStore } from '../../../Store/hooks/useStore'
import { AuctionModalHeader } from '../ModalHeader'

import { BidRecordListModalProps, BidRecordTableProps } from './interface'

const entrustVisible = ({
  status = AuctionMemberPageStatus.BIDDING
}: AuctionMemberPageItem) => {
  return Number(status) > BidRecordStatus.WAIT_ENTRUST_CONFIRM
}

const participantVisible = ({
  status = AuctionMemberPageStatus.BIDDING
}: AuctionMemberPageItem) => {
  return Number(status) >= BidRecordStatus.DEAL
}

const createColumns = (
  isEntrust: boolean,
  actionRender: BidRecordListModalProps['actionRender']
): BidRecordTableProps['columns'] => [
  {
    dataIndex: 'applyId',
    title: '席位号',
    render: getSeatNo
  },
  {
    dataIndex: 'memberName',
    title: '会员名称'
  },
  {
    dataIndex: 'bidTime',
    title: '出价时间',
    align: 'center'
  },
  {
    dataIndex: 'quantity',
    title: '出价数量',
    align: 'right'
  },
  {
    dataIndex: 'price',
    title: '报价',
    align: 'right'
  },
  {
    dataIndex: 'dealQuantity',
    title: '成交数量',
    align: 'right',
    render: (value, record) => {
      const visible = isEntrust
        ? entrustVisible(record)
        : participantVisible(record)
      return visible ? pretty(value) : '-'
    }
  },
  {
    dataIndex: 'dealPrice',
    title: '成交价',
    align: 'right',
    render: (value, record) => {
      const visible = isEntrust
        ? entrustVisible(record)
        : participantVisible(record)
      return visible ? pretty(value) : '-'
    }
  },
  {
    dataIndex: 'status',
    title: '状态',
    align: 'center',
    render(value: BidRecordStatus) {
      const statusText = isEntrust
        ? BidRecordStatusEntrustTextMap[value]
        : BidRecordStatusParticipantTextMap[value]
      return (
        <Tag color={BidRecordStatusColorMap[value]} variant='filled'>
          {statusText}
        </Tag>
      )
    }
  },
  {
    dataIndex: 'action',
    title: '操作',
    align: 'center',
    visible: () => isEntrust,
    render: actionRender
  }
]

export const BidRecordListModal = ({
  isEntrust = true,
  actionRender
}: BidRecordListModalProps) => {
  const {
    currentAuction,
    bidRecordListModalVisible,
    toggleBidRecordListModalVisible
  } = useAuctionListStore(state =>
    pick(state, [
      'currentAuction',
      'bidRecordListModalVisible',
      'toggleBidRecordListModalVisible'
    ])
  )

  const tableRef = useRef<RemoteTableStoreRef>(null)

  const title = (
    <AuctionModalHeader
      desc={currentAuction?.title}
      tableRef={tableRef}
      title='出价记录'
    />
  )

  const columns = useMemo(
    () => createColumns(isEntrust, actionRender),
    [isEntrust, actionRender]
  )

  const auctionId = currentAuction?.id
  const tableApi = isEntrust
    ? queryEntrustBidRecordListApi
    : queryParticipantBidRecordListApi
  return (
    <DraggableModal
      okText='关闭'
      open={bidRecordListModalVisible}
      title={title}
      width='80%'
      cancelButtonProps={{
        hidden: true
      }}
      onOpenChange={toggleBidRecordListModalVisible}
    >
      <RemoteTable
        ref={tableRef}
        api={tableApi}
        columns={columns}
        context={currentAuction}
        pagination={false}
        readyRequest={bidRecordListModalVisible && !!auctionId}
        refreshDeps={[bidRecordListModalVisible, auctionId]}
        rowKey='id'
        requestParams={{
          id: auctionId
        }}
        scroll={{
          y: 500
        }}
      />
    </DraggableModal>
  )
}
